import { useState } from "react";
import { fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ZoomIn, ZoomOut } from "lucide-react";

// Gantt à échelle dynamique : bandes horizontales positionnées par plage de dates.
const SCALES = { Compact: 6, Semaine: 14, Jour: 34 };
const COLORS = { "À faire": "#94a3b8", "En cours": "#076633", "Bloquée": "#EF4444", "Terminée": "#10B981", "Annulée": "#cbd5e1" };

export default function GanttChart({ tasks }) {
  const [scale, setScale] = useState("Semaine");
  const dated = tasks.filter((t) => t.date_debut && t.date_echeance);
  if (!dated.length) return <p className="text-sm text-slate-400 py-8 text-center">Aucune tâche avec dates de début et d'échéance pour afficher le Gantt.</p>;

  const dates = dated.flatMap((t) => [new Date(t.date_debut), new Date(t.date_echeance)]);
  const min = new Date(Math.min(...dates));
  const max = new Date(Math.max(...dates));
  const totalDays = Math.max(1, Math.round((max - min) / 86400000));
  const pxPerDay = SCALES[scale];
  const trackW = totalDays * pxPerDay + 40;
  const dayOffset = (d) => Math.round((new Date(d) - min) / 86400000) * pxPerDay;

  const months = [];
  let cur = new Date(min.getFullYear(), min.getMonth(), 1);
  while (cur <= max) { months.push(new Date(cur)); cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1); }

  const today = new Date();
  const todayLeft = today >= min && today <= max ? dayOffset(today) : null;

  return (
    <div>
      <div className="flex items-center justify-end gap-1.5 mb-3">
        <span className="text-xs text-slate-400 mr-1 font-mono">Échelle</span>
        <Button size="icon" variant="outline" className="h-8 w-8" data-testid="gantt-zoom-out"
          onClick={() => setScale((s) => (s === "Jour" ? "Semaine" : "Compact"))} disabled={scale === "Compact"}>
          <ZoomOut className="h-4 w-4" />
        </Button>
        {Object.keys(SCALES).map((s) => (
          <button key={s} onClick={() => setScale(s)} data-testid={`gantt-scale-${s}`}
            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${scale === s ? "bg-brand-600 text-white" : "text-slate-500 hover:bg-slate-100"}`}>{s}</button>
        ))}
        <Button size="icon" variant="outline" className="h-8 w-8" data-testid="gantt-zoom-in"
          onClick={() => setScale((s) => (s === "Compact" ? "Semaine" : "Jour"))} disabled={scale === "Jour"}>
          <ZoomIn className="h-4 w-4" />
        </Button>
      </div>

      <div className="overflow-x-auto scrollbar-thin">
        <div style={{ width: `calc(13rem + ${trackW}px)` }}>
          <div className="relative h-5 mb-2 ml-52">
            {months.map((m, i) => (
              <div key={i} className="absolute text-[10px] font-mono text-slate-400 whitespace-nowrap" style={{ left: `${dayOffset(m)}px` }}>
                {m.toLocaleDateString("fr-FR", { month: "short", year: "2-digit" })}
              </div>
            ))}
          </div>
          <div className="space-y-2 relative">
            {todayLeft !== null && (
              <div className="absolute top-0 bottom-0 w-px bg-rose-400 z-10" style={{ left: `calc(13rem + ${todayLeft}px)` }}>
                <span className="absolute -top-1 -translate-x-1/2 text-[9px] font-mono text-rose-500 bg-white px-1">auj.</span>
              </div>
            )}
            {dated.map((t) => {
              const left = dayOffset(t.date_debut);
              const width = Math.max(pxPerDay, dayOffset(t.date_echeance) - left + pxPerDay);
              return (
                <div key={t.id} className="flex items-center gap-2">
                  <div className="w-52 shrink-0 truncate text-xs font-medium text-slate-700 pr-2">{t.titre}</div>
                  <div className="relative flex-1 h-7 bg-slate-50 rounded" style={{ minWidth: `${trackW}px` }}>
                    <div className="absolute top-1 h-5 rounded flex items-center px-2 text-[10px] font-semibold text-white overflow-hidden whitespace-nowrap transition-all"
                      style={{ left: `${left}px`, width: `${width}px`, background: COLORS[t.statut] || "#076633" }}
                      title={`${fmtDate(t.date_debut)} → ${fmtDate(t.date_echeance)}`}>
                      {t.avancement || 0}%
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
